import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { RequirementStatus } from './status';
import { parseStatusFilter } from './status';

/**
 * The group screen's filter and selection, held in the URL.
 *
 * In the URL rather than in state so a trip out to a change screen and back
 * returns you to where you were, and so a single requirement can be linked to
 * directly. `replace` keeps the back button meaning "the previous screen"
 * rather than "the previous filter".
 */
export const useGroupDetailParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const setParam = useCallback(
    (key: string, value: string | undefined) => {
      const next = new URLSearchParams(searchParams);
      if (value === undefined || value === 'all') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setStatusFilter = useCallback(
    (value: string) => setParam('status', value),
    [setParam]
  );

  const setOpenRequirementId = useCallback(
    (value: string | undefined) => setParam('requirement', value),
    [setParam]
  );

  return {
    statusFilter: parseStatusFilter(searchParams.get('status')),
    setStatusFilter,
    openRequirementId: searchParams.get('requirement') ?? undefined,
    setOpenRequirementId,
  } satisfies {
    statusFilter: RequirementStatus | 'all';
    setStatusFilter: (value: string) => void;
    openRequirementId: string | undefined;
    setOpenRequirementId: (value: string | undefined) => void;
  };
};
